/* Notification Center — Hold Time category targets (RTCFR 2026-10-14, consolidated 2026-10-15).
 *
 * A Hold Time rule monitors the hold times a test's Test Guide configures — Before Prep
 * (HoldingTimeBeforePrep) and Before Analysis (HoldingTimeBeforeAnalysis). Each is a
 * SEPARATE target with its own Alert flag and Warn Before hours, saved and evaluated
 * independently; the grid shows a test ONCE with its entries stacked. The entries come
 * from GET /notification-rules/hold-time-catalog (dbo.AI_HoldTime_TestConfig_TVF — the
 * test's live Test Guide rows, the shortest value per type, > 0 hours), the SAME source
 * the alert clock reads. Storage: dbo.AI_NotificationRuleHoldTimeTarget + …HoldTimeWarn.
 *
 * The validation here MIRRORS the server's HoldTimeTargetRules for UX only — the
 * server is authoritative (CLAUDE.md §14b.5). Keep the two in step. */

/** The two hold-time types a Test Guide row configures. */
export type HoldTimeType = 'Prep' | 'Analysis';
export const HOLD_TIME_TYPES: readonly HoldTimeType[] = ['Prep', 'Analysis'];

/** "Before Prep" / "Before Analysis" — how a type reads on screen (matches the server). */
export function typeLabel(type: HoldTimeType | string): string {
  return type === 'Prep' ? 'Before Prep' : 'Before Analysis';
}

/** One configured hold time of one test (an entry the grid offers). */
export interface HoldTimeCatalogRow {
  testMethodId: string;
  holdTimeType: HoldTimeType;
  matrixId?: string | null;
  matrix?: string | null;
  test?: string | null;
  methodId?: string | null;
  method?: string | null;
  /** This type's hold time label, unit included — e.g. "48 hours", "6 months". */
  holdTime: string;
  /** This type's hold time in hours (what its warn values are compared against). */
  holdHours: number;
  /** The Test Guide row that governs this type. */
  testGuideId?: string | null;
  holdingTimeId?: string | null;
}

/** The units a Warn Before value may be entered in — the unit spinner cycles through these. */
export type WarnUnit = 'Days' | 'Hours' | 'Minutes';
export const WARN_UNITS: readonly WarnUnit[] = ['Days', 'Hours', 'Minutes'];
/** The unit a new warning starts in. */
export const DEFAULT_WARN_UNIT: WarnUnit = 'Hours';
const MINUTES_PER: Record<WarnUnit, number> = { Days: 1440, Hours: 60, Minutes: 1 };

/** One Warn Before threshold as entered: ONE whole number + ONE unit. "2 Days", "4 Hours" and
 *  "30 Minutes" are three separate warnings, never one combined duration. */
export interface NcWarn { value: number; unit: WarnUnit }

/** A warning's duration in minutes — what is compared, de-duplicated and scheduled. */
export function warnMinutes(w: NcWarn): number {
  return w.value * (MINUTES_PER[w.unit] ?? 0);
}

/** "2 Days", "1 Day", "30 Minutes". */
export function fmtWarn(w: NcWarn): string {
  return `${w.value} ${w.value === 1 ? w.unit.replace(/s$/, '') : w.unit}`;
}

/** "2 Days, 4 Hours, 30 Minutes" — longest first; "—" when there are none. */
export function fmtWarnings(list: readonly NcWarn[]): string {
  if (!list.length) return '—';
  // " · " not ", ": "2 Days, 10 Hours" reads as ONE combined duration (2 days 10 hours),
  // but these are separate warnings, each its own alert (owner report 2026-10-15).
  return sortWarnings(list).map(fmtWarn).join(' · ');
}

/** The warnings longest first — the order they fire in. */
export function sortWarnings(list: readonly NcWarn[]): NcWarn[] {
  return [...list].sort((a, b) => warnMinutes(b) - warnMinutes(a));
}

/** Plain-language meaning of an entry's warnings, for the tooltip: "2 separate alerts: 2 Days
 *  before and 10 Hours before the hold time ends." — so a list is never read as one sum. */
export function describeWarnings(list: readonly NcWarn[]): string {
  if (!list.length) return 'No warnings set.';
  const parts = sortWarnings(list).map((w) => `${fmtWarn(w)} before`);
  const joined = parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return list.length === 1
    ? `1 alert: ${joined} the hold time ends.`
    : `${list.length} separate alerts: ${joined} the hold time ends.`;
}

/** The numeric textbox: a whole number greater than 0, else NaN (blank, letters, decimals,
 *  negatives and 0 are all rejected). */
export function parseWarnValue(text: string): number {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return Number.NaN;
  const n = Number(t);
  return n > 0 && Number.isSafeInteger(n) ? n : Number.NaN;
}

/** The next unit in the spinner, wrapping — `+1` = up (larger), `-1` = down (smaller). */
export function cycleUnit(unit: WarnUnit, step: 1 | -1): WarnUnit {
  const order: WarnUnit[] = ['Minutes', 'Hours', 'Days'];
  const i = order.indexOf(unit);
  return order[(i + step + order.length) % order.length]!;
}

/** A rule's saved target — ONE type of one test. Write uses testMethodId / holdTimeType /
 *  enabled / warnings; the rest is the live display read back from the server. */
export interface NcHoldTimeTarget {
  testMethodId: string;
  holdTimeType: HoldTimeType;
  enabled: boolean;
  warnings: NcWarn[];
  matrix?: string | null;
  test?: string | null;
  method?: string | null;
  holdTime?: string | null;
  holdHours?: number | null;
  /** false = the test no longer has this hold time configured (kept and flagged). */
  exists?: boolean;
}

/** One entry's editable state. The map is keyed by {@link htKey} (test + type), so each
 *  type's Alert and warnings belong to that entry alone. */
export interface HoldTimeSel { enabled: boolean; warn: NcWarn[] }

/** The key of one (test, type) entry in a selection map. */
export function htKey(testMethodId: string, type: HoldTimeType | string): string {
  return `${testMethodId}|${type}`;
}
/** Split a {@link htKey} back into its test and type. */
export function parseHtKey(key: string): { testMethodId: string; holdTimeType: HoldTimeType } {
  const i = key.lastIndexOf('|');
  return { testMethodId: key.slice(0, i), holdTimeType: (key.slice(i + 1) as HoldTimeType) };
}

/** Same limits as the server (HoldTimeTargetRules). */
export const MAX_WARN_VALUES = 10;
export const NO_ENABLED_TARGET_MESSAGE = 'Enable at least one hold-time target.';

/** The category name that gets the Hold Time target grid (case- and
 *  space-insensitive, the same comparison the source → category auto-select uses). */
export const HOLD_TIME_CATEGORY = 'Hold Time';
export function isHoldTimeCategory(name: string | undefined | null): boolean {
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  return !!name && norm(name) === norm(HOLD_TIME_CATEGORY);
}

/** "Test / Method" — the test part of every label. */
export function testLabel(r: { test?: string | null; method?: string | null }): string {
  return r.method?.trim() ? `${r.test ?? 'Test'} / ${r.method}` : (r.test ?? 'Test');
}

/** "Test / Method · Before Prep" — how an entry is named in every message (matches the server). */
export function targetLabel(r: { test?: string | null; method?: string | null; holdTimeType?: HoldTimeType | string }): string {
  return r.holdTimeType ? `${testLabel(r)} · ${typeLabel(r.holdTimeType)}` : testLabel(r);
}

/** A hold-time label split for the aligned Hold Time column: "14 days" → { value: "14",
 *  unit: "days" }. A label with no leading number keeps it all in `unit`. */
export function splitHoldTime(label: string | null | undefined): { value: string; unit: string } {
  const m = /^\s*(\d+(?:\.\d+)?)\s*(.*)$/.exec(label ?? '');
  return m ? { value: m[1]!, unit: m[2]!.trim() } : { value: '', unit: (label ?? '').trim() };
}

/** Error for ONE warning about to be added, or null. `existing` are the entry's other
 *  warnings; a duplicate is any warning of the SAME DURATION (1 Day = 24 Hours). */
export function warnError(w: NcWarn, holdHours: number | null | undefined, holdTime: string | null | undefined,
  existing: readonly NcWarn[]): string | null {
  if (!Number.isSafeInteger(w.value) || w.value <= 0) return 'Enter a whole number greater than 0.';
  if (!WARN_UNITS.includes(w.unit)) return 'Choose Days, Hours or Minutes.';
  const mins = warnMinutes(w);
  if (holdHours != null && mins >= holdHours * 60) return `Must be less than the hold time (${holdTime ?? `${holdHours} hours`}).`;
  const same = existing.find((e) => warnMinutes(e) === mins);
  if (same) return same.unit === w.unit ? 'Already added.' : `Same as ${fmtWarn(same)}.`;
  return null;
}

/** First error in an entry's whole warning list (the server's message shape), or null. */
export function validateWarnings(list: readonly NcWarn[],
  row: Pick<HoldTimeCatalogRow, 'holdHours' | 'holdTime' | 'test' | 'method' | 'holdTimeType'>): string | null {
  if (list.length > MAX_WARN_VALUES) return `${targetLabel(row)}: at most ${MAX_WARN_VALUES} warn times are allowed.`;
  for (const [i, w] of list.entries()) {
    const err = warnError(w, row.holdHours, row.holdTime, list.slice(0, i));
    if (err) return `${targetLabel(row)}: warn time ${fmtWarn(w)} — ${err}`;
  }
  return null;
}

/** The Target step / Rule Detail gate: at least one Alert on, every warn list valid
 *  (each against ITS OWN type's hold time). */
export function holdTimeTargetsError(sel: Record<string, HoldTimeSel>, catalog: readonly HoldTimeCatalogRow[]): string | null {
  const byKey = new Map(catalog.map((c) => [htKey(c.testMethodId, c.holdTimeType), c]));
  if (!Object.values(sel).some((s) => s.enabled)) return NO_ENABLED_TARGET_MESSAGE;
  for (const [key, s] of Object.entries(sel)) {
    const row = byKey.get(key);
    if (!row || !s.warn.length) continue;
    const err = validateWarnings(s.warn, row);
    if (err) return err;
  }
  return null;
}

/** What is SAVED: every entry whose Alert is on or that carries warn values (an off
 *  entry with warnings keeps them, so turning it back on restores them). Entries the
 *  catalog no longer offers are dropped (the server would refuse them). */
export function toSavedTargets(sel: Record<string, HoldTimeSel>, catalog: readonly HoldTimeCatalogRow[]): NcHoldTimeTarget[] {
  const known = new Set(catalog.map((c) => htKey(c.testMethodId, c.holdTimeType)));
  return Object.entries(sel)
    .filter(([key, s]) => known.has(key) && (s.enabled || s.warn.length > 0))
    .map(([key, s]) => ({ ...parseHtKey(key), enabled: s.enabled, warnings: [...s.warn].sort((a, b) => warnMinutes(b) - warnMinutes(a)) }));
}

/** Seed the editable state from a rule's saved targets (Edit / View). */
export function selFromTargets(targets: readonly NcHoldTimeTarget[] | undefined): Record<string, HoldTimeSel> {
  return Object.fromEntries((targets ?? []).map((t) => [htKey(t.testMethodId, t.holdTimeType), { enabled: t.enabled, warn: [...t.warnings] }]));
}

/** Display string for one entry, e.g. "BOD5 / SM 5210B · Before Prep (On · 24, 12 Hrs)". */
export function describeTarget(label: string, s: HoldTimeSel | undefined): string {
  if (!s) return `${label} (—)`;
  return `${label} (${s.enabled ? 'On' : 'Off'} · ${fmtWarnings(s.warn)})`;
}

/** One entry of a grid row: one hold-time type of the row's test. */
export interface HoldTimeEntry {
  key: string;
  holdTimeType: HoldTimeType;
  holdTime: string;
  holdHours: number | null;
  /** false = a saved entry whose hold time is no longer configured. */
  exists: boolean;
}

/** One grid row = one test (Matrix + Test + Method shown ONCE) with its entries stacked,
 *  Before Prep first. */
export interface HoldTimeTestRow {
  id: string;
  matrix: string;
  test: string;
  method: string;
  entries: HoldTimeEntry[];
}

/** Group the catalog (one entry per test + type) into one row per test, adding any saved
 *  entry the catalog no longer offers — flagged, never silently dropped. */
export function groupByTest(catalog: readonly HoldTimeCatalogRow[], saved?: readonly NcHoldTimeTarget[]): HoldTimeTestRow[] {
  const rows = new Map<string, HoldTimeTestRow>();
  const order = (t: HoldTimeType) => (t === 'Prep' ? 0 : 1);
  for (const c of catalog) {
    let row = rows.get(c.testMethodId);
    if (!row) {
      row = { id: c.testMethodId, matrix: c.matrix ?? '', test: c.test ?? '', method: c.method ?? '', entries: [] };
      rows.set(c.testMethodId, row);
    }
    const key = htKey(c.testMethodId, c.holdTimeType);
    if (!row.entries.some((e) => e.key === key))
      row.entries.push({ key, holdTimeType: c.holdTimeType, holdTime: c.holdTime, holdHours: c.holdHours, exists: true });
  }
  for (const t of saved ?? []) {
    const key = htKey(t.testMethodId, t.holdTimeType);
    let row = rows.get(t.testMethodId);
    if (row?.entries.some((e) => e.key === key)) continue;
    if (!row) {
      row = { id: t.testMethodId, matrix: t.matrix ?? '', test: t.test ?? '(unknown test)', method: t.method ?? '', entries: [] };
      rows.set(t.testMethodId, row);
    }
    row.entries.push({ key, holdTimeType: t.holdTimeType, holdTime: 'No longer configured', holdHours: null, exists: false });
  }
  for (const row of rows.values()) row.entries.sort((a, b) => order(a.holdTimeType) - order(b.holdTimeType));
  return [...rows.values()];
}
